import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import * as XLSX from 'xlsx';
import httpService from "../../services/httpService";
import posthog from 'posthog-js';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Collapse from 'react-bootstrap/Collapse';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Row from 'react-bootstrap/Row';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Tooltip from 'react-bootstrap/Tooltip';
import { FaSearch } from 'react-icons/fa';
import eventBus from '../../services/eventBus';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/light.css';

const email_subject_template = `You're Invited to Join {{title}}`;
const email_template = `<p>Hi {{first_name}},</p>

<p>You have been invited to join <strong>{{title}}</strong> on Podsy.</p>

<h2>About the Club</h2>
<p>{{description}}</p>

<h3>What you will get as a member</h3>
<ul>
    <li>Access to curated podcast episodes and guided discussions</li>
    <li>Opportunities to practise leadership, communication, and critical thinking</li>
    <li>A collaborative space to connect with like-minded listeners</li>
</ul>

<h3>Key dates</h3>
<p><strong>Club starts:</strong> {{start_date}}<br>
<strong>Invitation expires:</strong> {{invite_expiry_date}}</p>

<p><a href="{{invite_url}}">Accept Invitation</a></p>

<p>This invitation was sent to {{email}} and is personal to you. Please accept it before the expiration date to secure your place.</p>

<p>Warm regards,<br>
The {{title}} Team</p>`;

const quillModules = {
    toolbar: [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
    ],
};

const AccessCode = ({ code }) => {
    const [revealed, setRevealed] = useState(false);
    const masked = code.replace(/./g, '•');

    const handleClick = () => {
        if (!revealed) {
            setRevealed(true);
        } else {
            navigator.clipboard.writeText(code);
        }
    };

    return (
        <div className="mt-1">
            <small className="text-muted d-block mb-1">Invite code</small>
            <OverlayTrigger
                placement="left"
                overlay={<Tooltip>{revealed ? 'Click to copy' : 'Click to reveal'}</Tooltip>}
            >
                <code
                    className="small"
                    onClick={handleClick}
                    style={{ cursor: 'pointer' }}
                >
                    {revealed ? code : masked}
                </code>
            </OverlayTrigger>
        </div>
    );
};

const ClubInvites = ({ clubGuid, title, description, image }) => {
    const [activeTab, setActiveTab] = useState('sent');

    // --- Sent invites state ---
    const [loading, setLoading] = useState(true);
    const [invites, setInvites] = useState([]);
    const [invitesError, setInvitesError] = useState(null);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("desc");
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [pagination, setPagination] = useState({
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
    });

    // --- Resend invite modal state ---
    const [showResendModal, setShowResendModal] = useState(false);
    const [resendingInvite, setResendingInvite] = useState(null);
    const resendingInviteRef = useRef(null);
    const [resendFormData, setResendFormData] = useState({ email_subject: '', email_content: '', expires_at: '' });
    const [resendFormErrors, setResendFormErrors] = useState({});
    const [savingResend, setSavingResend] = useState(false);
    const [resendSaveError, setResendSaveError] = useState(null);

    // --- Templates state ---
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [templatesPagination, setTemplatesPagination] = useState({
        total: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false
    });
    const [templatesSearch, setTemplatesSearch] = useState('');
    const [templatesSort, setTemplatesSort] = useState('asc');
    const [templatesType, setTemplatesType] = useState('');
    const [templatesPageNumber, setTemplatesPageNumber] = useState(1);
    const [templatesPageSize, setTemplatesPageSize] = useState(20);
    const [templatesError, setTemplatesError] = useState(null);

    // --- Invite Codes state ---
    const [inviteCodesLoading, setInviteCodesLoading] = useState(false);
    const [inviteCodes, setInviteCodes] = useState([]);
    const [inviteCodesPagination, setInviteCodesPagination] = useState({
        total: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false
    });
    const [inviteCodesPageNumber, setInviteCodesPageNumber] = useState(1);
    const [inviteCodesPageSize, setInviteCodesPageSize] = useState(20);
    const [inviteCodesSort, setInviteCodesSort] = useState('desc');
    const [inviteCodesError, setInviteCodesError] = useState(null);

    // --- Invite Code modal state ---
    const [showInviteCodeModal, setShowInviteCodeModal] = useState(false);
    const [editingInviteCode, setEditingInviteCode] = useState(null);
    const editingInviteCodeRef = useRef(null);
    const [inviteCodeFormData, setInviteCodeFormData] = useState({ code: '', redeem_limit: 1, expires_at: '', is_active: true });
    const [inviteCodeFormErrors, setInviteCodeFormErrors] = useState({});
    const [savingInviteCode, setSavingInviteCode] = useState(false);
    const [inviteCodeSaveError, setInviteCodeSaveError] = useState(null);
    const [confirmDeleteCode, setConfirmDeleteCode] = useState(false);
    const [deletingCode, setDeletingCode] = useState(false);

    // --- Create/edit template modal state ---
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null); // null = create mode, template object = edit mode
    const editingTemplateRef = useRef(null);
    const [templateFormData, setTemplateFormData] = useState({ name: '', description: '', subject: '', content: '' });
    const [templateFormErrors, setTemplateFormErrors] = useState({});
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [templateSaveError, setTemplateSaveError] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletingTemplate, setDeletingTemplate] = useState(false);

    // --- Send invite state ---
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [sendResult, setSendResult] = useState(null); // { created_count, skipped_count, skipped }
    const [recipients, setRecipients] = useState([{ email: '', first_name: '', last_name: '' }]);
    const [inviteFormData, setInviteFormData] = useState({
        email_subject: email_subject_template,
        email_content: email_template,
        expires_at: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [showPreview, setShowPreview] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importPreview, setImportPreview] = useState(null); // parsed rows before confirming
    const [importError, setImportError] = useState(null);
    const fileInputRef = useRef(null);
    const [firstEpisodeStartDate, setFirstEpisodeStartDate] = useState(null);

    // Fetch sent invites
    useEffect(() => {
        if (clubGuid) fetchInvites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clubGuid, pageNumber, pageSize, sort]);

    // Fetch the club's episodes so {{start_date}} can reflect the first episode's start date/time
    useEffect(() => {
        if (!clubGuid) return;
        httpService.get(`/dash/club/${clubGuid}/episodes`)
            .then((res) => {
                const episodes = res.data.episodes || [];
                const startDates = episodes
                    .map((ep) => ep.pivot?.start_date_time)
                    .filter(Boolean)
                    .sort();
                setFirstEpisodeStartDate(startDates[0] || null);
            })
            .catch(() => setFirstEpisodeStartDate(null));
    }, [clubGuid]);

    // Fetch templates when tab is active or params change
    useEffect(() => {
        if (activeTab === 'templates') fetchTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, templatesPageNumber, templatesPageSize, templatesSort, templatesType]);

    // Fetch invite codes when tab is active or params change
    useEffect(() => {
        if (activeTab === 'codes') fetchInviteCodes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, inviteCodesPageNumber, inviteCodesPageSize, inviteCodesSort]);

    const fetchInvites = () => {
        setLoading(true);
        setInvitesError(null);
        const params = {
            pageNumber,
            pageSize,
            sort,
            ...(search && { search })
        };
        httpService.get(`/dash/club/${clubGuid}/invites`, { params })
            .then((res) => {
                setInvites(res.data.data || []);
                setPagination(res.data.pagination);
            })
            .catch((err) => {
                setInvitesError(err.response?.data?.message || "Error loading invites");
            })
            .finally(() => setLoading(false));
    };

    const fetchTemplates = () => {
        setTemplatesLoading(true);
        setTemplatesError(null);
        const params = {
            pageNumber: templatesPageNumber,
            pageSize: templatesPageSize,
            sort: templatesSort,
            ...(templatesSearch && { search: templatesSearch }),
            ...(templatesType && { type: templatesType })
        };
        httpService.get('/dash/invite-templates', { params })
            .then((res) => {
                setTemplates(res.data.data || []);
                setTemplatesPagination(res.data.pagination);
            })
            .catch((err) => {
                setTemplatesError(err.response?.data?.message || "Error loading templates");
            })
            .finally(() => setTemplatesLoading(false));
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPageNumber(1);
        fetchInvites();
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value));
        setPageNumber(1);
    };

    const handlePreviousPage = () => {
        if (pagination.hasPreviousPage) setPageNumber(pageNumber - 1);
    };

    const handleNextPage = () => {
        if (pagination.hasNextPage) setPageNumber(pageNumber + 1);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setInviteFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleContentChange = (value) => {
        setInviteFormData(prev => ({ ...prev, email_content: value }));
    };

    const handleRecipientChange = (index, field, value) => {
        setRecipients(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
    };

    const addRecipient = () => {
        if (recipients.length < 50) {
            setRecipients(prev => [...prev, { email: '', first_name: '', last_name: '' }]);
        }
    };

    const removeRecipient = (index) => {
        setRecipients(prev => prev.filter((_, i) => i !== index));
    };

    const handleFileImport = (e) => {
        const file = e.target.files[0];
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (!file) return;
        setImportError(null);
        setImportPreview(null);

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

                if (rows.length < 2) {
                    setImportError('The file appears to be empty or has no data rows.');
                    return;
                }

                const headers = rows[0].map(h => String(h).toLowerCase().trim().replace(/[^a-z0-9_]/g, ''));
                const emailIdx = headers.findIndex(h => h === 'email' || h === 'emailaddress');
                const firstIdx = headers.findIndex(h => h === 'firstname' || h === 'first_name' || h === 'first');
                const lastIdx = headers.findIndex(h => h === 'lastname' || h === 'last_name' || h === 'last');

                if (emailIdx === -1) {
                    setImportError('Could not find an "email" column. Make sure the first row contains column headers.');
                    return;
                }

                const parsed = rows.slice(1)
                    .map(row => ({
                        email: String(row[emailIdx] || '').trim(),
                        first_name: firstIdx !== -1 ? String(row[firstIdx] || '').trim() : '',
                        last_name: lastIdx !== -1 ? String(row[lastIdx] || '').trim() : ''
                    }))
                    .filter(r => r.email);

                if (parsed.length === 0) {
                    setImportError('No valid email addresses found in the file.');
                    return;
                }

                setImportPreview(parsed);
            } catch {
                setImportError('Failed to parse the file. Make sure it is a valid CSV or XLSX.');
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleImportConfirm = () => {
        if (!importPreview) return;
        const capped = importPreview.slice(0, 50);
        setRecipients(capped);
        setShowImportModal(false);
        setImportPreview(null);
        setImportError(null);
    };

    const handleCloseImportModal = () => {
        setShowImportModal(false);
        setImportPreview(null);
        setImportError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const resetForm = () => {
        setRecipients([{ email: '', first_name: '', last_name: '' }]);
        setInviteFormData({
            email_subject: email_subject_template,
            email_content: email_template,
            expires_at: ''
        });
        setFormErrors({});
        setShowPreview(false);
        setSendError(null);
        setSendResult(null);
        setImportError(null);
        setImportPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSendInvite = async (e) => {
        e.preventDefault();
        setSending(true);
        setFormErrors({});
        setSendError(null);
        setSendResult(null);

        try {
            const payload = {
                recipients: recipients
                    .filter(r => r.email.trim())
                    .map(r => ({
                        email: r.email.trim(),
                        ...(r.first_name.trim() && { first_name: r.first_name.trim() }),
                        ...(r.last_name.trim() && { last_name: r.last_name.trim() })
                    })),
                email_subject: inviteFormData.email_subject.trim(),
                email_content: inviteFormData.email_content.trim(),
                expires_at: inviteFormData.expires_at || null
            };

            const res = await httpService.post(`/dash/club/${clubGuid}/invites`, payload);
            posthog.capture('invite_sent', {
                club_guid: clubGuid,
                recipient_count: payload.recipients.length,
            });
            setSendResult(res.data.data);
            setRecipients([{ email: '', first_name: '', last_name: '' }]);
            fetchInvites();
        } catch (err) {
            if (err.response?.data?.errors) {
                setFormErrors(err.response.data.errors);
            }
            setSendError(err.response?.data?.message || 'Failed to send invites');
        } finally {
            setSending(false);
        }
    };

    const applyTemplate = (template) => {
        setInviteFormData(prev => ({
            ...prev,
            email_subject: template.subject,
            email_content: template.content
        }));
        setActiveTab('send');
    };

    const fetchInviteCodes = () => {
        setInviteCodesLoading(true);
        setInviteCodesError(null);
        httpService.get(`/dash/club/${clubGuid}/invite-codes`, {
            params: { pageNumber: inviteCodesPageNumber, pageSize: inviteCodesPageSize, sort: inviteCodesSort }
        })
            .then((res) => {
                setInviteCodes(res.data.data || []);
                setInviteCodesPagination(res.data.pagination);
            })
            .catch((err) => {
                setInviteCodesError(err.response?.data?.message || 'Error loading invite codes');
            })
            .finally(() => setInviteCodesLoading(false));
    };

    const handleOpenInviteCodeModal = (code = null) => {
        editingInviteCodeRef.current = code;
        setEditingInviteCode(code);
        setInviteCodeFormData(code
            ? { code: code.code, redeem_limit: code.redeem_limit, expires_at: code.expires_at ? code.expires_at.slice(0, 16) : '', is_active: code.is_active }
            : { code: '', redeem_limit: 1, expires_at: '', is_active: true }
        );
        setInviteCodeFormErrors({});
        setInviteCodeSaveError(null);
        setConfirmDeleteCode(false);
        setShowInviteCodeModal(true);
    };

    const handleCloseInviteCodeModal = () => {
        editingInviteCodeRef.current = null;
        setShowInviteCodeModal(false);
        setEditingInviteCode(null);
        setConfirmDeleteCode(false);
    };

    const handleSaveInviteCode = async (e) => {
        e.preventDefault();
        setSavingInviteCode(true);
        setInviteCodeFormErrors({});
        setInviteCodeSaveError(null);

        const payload = {
            redeem_limit: parseInt(inviteCodeFormData.redeem_limit) || 1,
            expires_at: inviteCodeFormData.expires_at || null,
            is_active: inviteCodeFormData.is_active
        };

        try {
            if (editingInviteCodeRef.current) {
                await httpService.put(`/dash/club/${clubGuid}/invite-codes/${editingInviteCodeRef.current.guid}`, payload);
            } else {
                await httpService.post(`/dash/club/${clubGuid}/invite-codes`, { ...payload, code: inviteCodeFormData.code.trim() });
            }
            setShowInviteCodeModal(false);
            fetchInviteCodes();
        } catch (err) {
            if (err.response?.data?.errors) setInviteCodeFormErrors(err.response.data.errors);
            setInviteCodeSaveError(err.response?.data?.message || 'Failed to save invite code');
        } finally {
            setSavingInviteCode(false);
        }
    };

    const handleDeleteInviteCode = async () => {
        setDeletingCode(true);
        try {
            await httpService.delete(`/dash/club/${clubGuid}/invite-codes/${editingInviteCodeRef.current.guid}`);
            editingInviteCodeRef.current = null;
            setShowInviteCodeModal(false);
            setEditingInviteCode(null);
            setConfirmDeleteCode(false);
            fetchInviteCodes();
        } catch (err) {
            setInviteCodeSaveError(err.response?.data?.message || 'Failed to delete invite code');
        } finally {
            setDeletingCode(false);
        }
    };

    const handleOpenResendModal = (invite) => {
        resendingInviteRef.current = invite;
        setResendingInvite(invite);
        setResendFormData({
            email_subject: invite.email_subject || email_subject_template,
            email_content: invite.email_content || email_template,
            expires_at: ''
        });
        setResendFormErrors({});
        setResendSaveError(null);
        setShowResendModal(true);
    };

    const handleCloseResendModal = () => {
        resendingInviteRef.current = null;
        setShowResendModal(false);
        setResendingInvite(null);
    };

    const handleResendSubmit = async (e) => {
        e.preventDefault();
        setSavingResend(true);
        setResendFormErrors({});
        setResendSaveError(null);

        const payload = {
            email_subject: resendFormData.email_subject,
            email_content: resendFormData.email_content,
            expires_at: resendFormData.expires_at || null
        };

        try {
            await httpService.post(`/dash/club/${clubGuid}/invites/${resendingInviteRef.current.guid}/resend`, payload);
            setShowResendModal(false);
            setResendingInvite(null);
            fetchInvites();
        } catch (err) {
            if (err.response?.data?.errors) setResendFormErrors(err.response.data.errors);
            setResendSaveError(err.response?.data?.message || 'Failed to resend invite');
        } finally {
            setSavingResend(false);
        }
    };

    const handleOpenTemplateModal = (template = null) => {
        editingTemplateRef.current = template;
        setEditingTemplate(template);
        setTemplateFormData(template
            ? { name: template.name, description: template.description || '', subject: template.subject, content: template.content }
            : { name: '', description: '', subject: '', content: '' }
        );
        setTemplateFormErrors({});
        setTemplateSaveError(null);
        setShowTemplateModal(true);
    };

    const handleCloseTemplateModal = () => {
        editingTemplateRef.current = null;
        setShowTemplateModal(false);
        setEditingTemplate(null);
        setConfirmDelete(false);
    };

    const handleDeleteTemplate = async () => {
        setDeletingTemplate(true);
        try {
            await httpService.delete(`/dash/invite-templates/${editingTemplateRef.current.guid}`);
            editingTemplateRef.current = null;
            setShowTemplateModal(false);
            setEditingTemplate(null);
            setConfirmDelete(false);
            fetchTemplates();
        } catch (err) {
            setTemplateSaveError(err.response?.data?.message || 'Failed to delete template');
        } finally {
            setDeletingTemplate(false);
        }
    };

    const handleTemplateFormChange = (e) => {
        const { name, value } = e.target;
        setTemplateFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveTemplate = async (e) => {
        e.preventDefault();
        setSavingTemplate(true);
        setTemplateFormErrors({});
        setTemplateSaveError(null);

        const payload = {
            name: templateFormData.name.trim(),
            description: templateFormData.description.trim() || null,
            subject: templateFormData.subject.trim(),
            content: templateFormData.content.trim()
        };

        try {
            if (editingTemplateRef.current) {
                await httpService.put(`/dash/invite-templates/${editingTemplateRef.current.guid}`, payload);
            } else {
                await httpService.post('/dash/invite-templates', payload);
            }
            setShowTemplateModal(false);
            fetchTemplates();
        } catch (err) {
            if (err.response?.data?.errors) {
                setTemplateFormErrors(err.response.data.errors);
            }
            setTemplateSaveError(err.response?.data?.message || 'Failed to save template');
        } finally {
            setSavingTemplate(false);
        }
    };

    const replaceTokens = (text) => {
        const formatDateTime = (dateString) => {
            if (!dateString) return '[Not set]';
            return new Date(dateString).toLocaleString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric',
                hour: 'numeric', minute: '2-digit'
            });
        };
        const firstRecipient = recipients[0] || {};
        return text
            .replace(/{{title}}/g, title || '[Club Title]')
            .replace(/{{description}}/g, description || '[Club Description]')
            .replace(/{{email}}/g, firstRecipient.email || '[Email Address]')
            .replace(/{{start_date}}/g, firstEpisodeStartDate ? formatDateTime(firstEpisodeStartDate) : '[Not set]')
            .replace(/{{invite_expiry_date}}/g, formatDateTime(inviteFormData.expires_at))
            .replace(/{{first_name}}/g, firstRecipient.first_name || '[First Name]')
            .replace(/{{last_name}}/g, firstRecipient.last_name || '[Last Name]')
            .replace(/{{access_code}}/g, '[Access Code]')
            .replace(/{{invite_url}}/g, '[Invite URL]');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { bg: 'warning', text: 'Pending' },
            accepted: { bg: 'success', text: 'Accepted' },
            declined: { bg: 'danger', text: 'Declined' },
            expired: { bg: 'secondary', text: 'Expired' }
        };
        const config = statusConfig[status] || { bg: 'secondary', text: status };
        return <Badge bg={config.bg}>{config.text}</Badge>;
    };

    return (
        <div className="py-3 component-ClubInvites">
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">

                {/* ── Tab 1: Sent Invites ── */}
                <Tab eventKey="sent" title={`Sent Invites${pagination.total > 0 ? ` (${pagination.total})` : ''}`}>
                    {invitesError && <Alert variant="danger">{invitesError}</Alert>}

                    <Row className="mb-3 align-items-center">
                        <Col md={5}>
                            <Form onSubmit={handleSearchSubmit}>
                                <InputGroup size="sm">
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by name or email..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <Button variant="primary" type="submit">
                                        <FaSearch />
                                    </Button>
                                </InputGroup>
                            </Form>
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                size="sm"
                                value={sort}
                                onChange={(e) => { setSort(e.target.value); setPageNumber(1); }}
                            >
                                <option value="desc">↓ Newest</option>
                                <option value="asc">↑ Oldest</option>
                            </Form.Select>
                        </Col>
                        <Col md={4} className="d-flex justify-content-end">
                            <Form.Select size="sm" value={pageSize} onChange={handlePageSizeChange} style={{ width: 'auto' }}>
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                            </Form.Select>
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    ) : invites.length === 0 ? (
                        <Alert variant="info">
                            No invitations found. Use the "Send New Invite" tab to get started!
                        </Alert>
                    ) : (
                        <>
                            {invites.map((invite) => (
                                <Card key={invite.id} className="mb-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <h6 className="mb-1">
                                                    {[invite.first_name, invite.last_name].filter(Boolean).join(" ") || invite.email}
                                                </h6>
                                                <small className="text-muted d-block">{invite.email}</small>
                                                <small className="text-muted">
                                                    Sent: {formatDate(invite.last_sent_at || invite.created_at)}
                                                    {invite.last_sent_at && invite.last_sent_at !== invite.created_at && <> (resent)</>}
                                                    {invite.accepted_at && <> | Accepted: {formatDate(invite.accepted_at)}</>}
                                                    {invite.expires_at && <> | Expires: {formatDate(invite.expires_at)}</>}
                                                </small>
                                            </div>
                                            <div className="text-center px-3">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handleOpenResendModal(invite)}
                                                    disabled={invite.status === 'accepted'}
                                                >
                                                    Resend Invite
                                                </Button>
                                            </div>
                                            <div className="text-end">
                                                {getStatusBadge(invite.is_expired ? 'expired' : invite.status)}
                                                <AccessCode code={invite.access_code} />
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}

                            <Row className="mt-3">
                                <Col className="d-flex justify-content-between align-items-center">
                                    <div>
                                        Showing {(pageNumber - 1) * pageSize + 1} to {Math.min(pageNumber * pageSize, pagination.total)} of {pagination.total} invites
                                    </div>
                                    <div>
                                        <Button variant="outline-primary" size="sm" onClick={handlePreviousPage} disabled={!pagination.hasPreviousPage} className="me-2">
                                            Previous
                                        </Button>
                                        <span className="mx-2">Page {pageNumber} of {pagination.totalPages}</span>
                                        <Button variant="outline-primary" size="sm" onClick={handleNextPage} disabled={!pagination.hasNextPage}>
                                            Next
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Tab>

                {/* ── Tab 2: Invite Templates ── */}
                <Tab eventKey="templates" title="Invite Templates">
                    <Row className="mb-3 align-items-center">
                        <Col md={4} className="mb-2 mb-md-0">
                            <Form onSubmit={(e) => { e.preventDefault(); setTemplatesPageNumber(1); fetchTemplates(); }}>
                                <InputGroup size="sm">
                                    <Form.Control
                                        type="text"
                                        placeholder="Search templates..."
                                        value={templatesSearch}
                                        onChange={(e) => setTemplatesSearch(e.target.value)}
                                    />
                                    <Button variant="primary" type="submit"><FaSearch /></Button>
                                </InputGroup>
                            </Form>
                        </Col>
                        <Col md={3} className="mb-2 mb-md-0">
                            <Form.Select
                                size="sm"
                                value={templatesType}
                                onChange={(e) => { setTemplatesType(e.target.value); setTemplatesPageNumber(1); }}
                            >
                                <option value="">All types</option>
                                <option value="default">Default</option>
                                <option value="custom">My templates</option>
                            </Form.Select>
                        </Col>
                        <Col md={2} className="mb-2 mb-md-0">
                            <Form.Select
                                size="sm"
                                value={templatesSort}
                                onChange={(e) => { setTemplatesSort(e.target.value); setTemplatesPageNumber(1); }}
                            >
                                <option value="asc">↑ Oldest</option>
                                <option value="desc">↓ Newest</option>
                            </Form.Select>
                        </Col>
                        <Col md={1} className="mb-2 mb-md-0">
                            <Form.Select
                                size="sm"
                                value={templatesPageSize}
                                onChange={(e) => { setTemplatesPageSize(parseInt(e.target.value)); setTemplatesPageNumber(1); }}
                            >
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                            </Form.Select>
                        </Col>
                        <Col md={2} className="mb-2 mb-md-0 d-flex justify-content-end">
                            <Button variant="primary" size="sm" onClick={() => handleOpenTemplateModal()}>
                                New Template
                            </Button>
                        </Col>
                    </Row>

                    {templatesError && <Alert variant="danger">{templatesError}</Alert>}

                    {templatesLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    ) : templates.length === 0 ? (
                        <Alert variant="info">No templates found.</Alert>
                    ) : (
                        <>
                            {templates.map((template) => (
                                <Card key={template.id} className="mb-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <h6 className="mb-0">{template.name}</h6>
                                                    <Badge bg={template.is_default ? 'secondary' : 'primary'}>
                                                        {template.is_default ? 'Default' : 'Custom'}
                                                    </Badge>
                                                </div>
                                                {template.description && (
                                                    <p className="text-muted small mb-1">{template.description}</p>
                                                )}
                                                <small className="text-muted">Subject: {template.subject}</small>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <Button variant="outline-primary" size="sm" onClick={() => applyTemplate(template)}>
                                                    Use template
                                                </Button>
                                                {!template.is_default && (
                                                    <Button variant="outline-secondary" size="sm" onClick={() => handleOpenTemplateModal(template)}>
                                                        Edit
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}

                            <Row className="mt-3">
                                <Col className="d-flex justify-content-between align-items-center">
                                    <div>
                                        Showing {(templatesPageNumber - 1) * templatesPageSize + 1} to {Math.min(templatesPageNumber * templatesPageSize, templatesPagination.total)} of {templatesPagination.total} templates
                                    </div>
                                    <div>
                                        <Button variant="outline-primary" size="sm" onClick={() => setTemplatesPageNumber(n => n - 1)} disabled={!templatesPagination.hasPreviousPage} className="me-2">
                                            Previous
                                        </Button>
                                        <span className="mx-2">Page {templatesPageNumber} of {templatesPagination.totalPages}</span>
                                        <Button variant="outline-primary" size="sm" onClick={() => setTemplatesPageNumber(n => n + 1)} disabled={!templatesPagination.hasNextPage}>
                                            Next
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Tab>

                {/* ── Tab 3: Send New Invite ── */}
                <Tab eventKey="send" title="Send New Invite">
                    {sendError && <Alert variant="danger">{sendError}</Alert>}
                    {sendResult && (
                        <Alert variant="success" onClose={() => setSendResult(null)} dismissible>
                            <strong>{sendResult.created_count} invite{sendResult.created_count !== 1 ? 's' : ''} sent successfully.</strong>
                            {sendResult.skipped_count > 0 && (
                                <div className="mt-1 small">
                                    {sendResult.skipped_count} skipped (already have an active invite): {sendResult.skipped.join(', ')}
                                </div>
                            )}
                        </Alert>
                    )}

                    <Form onSubmit={handleSendInvite}>
                        {/* Recipients */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0 fw-semibold">Recipients ({recipients.length}/50)</Form.Label>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        type="button"
                                        onClick={() => setShowImportModal(true)}
                                        disabled={sending}
                                    >
                                        Import CSV / XLSX
                                    </Button>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        type="button"
                                        onClick={addRecipient}
                                        disabled={sending || recipients.length >= 50}
                                    >
                                        + Add recipient
                                    </Button>
                                </div>
                            </div>
                            {importError && (
                                <Alert variant="warning" dismissible onClose={() => setImportError(null)} className="py-2 small">
                                    {importError}
                                </Alert>
                            )}

                            {recipients.map((recipient, index) => (
                                <Row key={index} className="mb-2 align-items-start">
                                    <Col md={5}>
                                        <Form.Control
                                            type="email"
                                            placeholder="Email address *"
                                            value={recipient.email}
                                            onChange={(e) => handleRecipientChange(index, 'email', e.target.value)}
                                            disabled={sending}
                                            isInvalid={!!formErrors[`recipients.${index}.email`]}
                                            required
                                            size="sm"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors[`recipients.${index}.email`]}
                                        </Form.Control.Feedback>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Control
                                            type="text"
                                            placeholder="First name"
                                            value={recipient.first_name}
                                            onChange={(e) => handleRecipientChange(index, 'first_name', e.target.value)}
                                            disabled={sending}
                                            size="sm"
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Last name"
                                            value={recipient.last_name}
                                            onChange={(e) => handleRecipientChange(index, 'last_name', e.target.value)}
                                            disabled={sending}
                                            size="sm"
                                        />
                                    </Col>
                                    <Col md={1} className="d-flex align-items-center">
                                        {recipients.length > 1 && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                type="button"
                                                onClick={() => removeRecipient(index)}
                                                disabled={sending}
                                            >
                                                ×
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            ))}
                        </div>

                        <Form.Group className="mb-3" controlId="inviteExpiresAt">
                            <Form.Label>Expires At</Form.Label>
                            <Flatpickr
                                className={`form-control${formErrors.expires_at ? ' is-invalid' : ''}`}
                                value={inviteFormData.expires_at || undefined}
                                options={{ enableTime: true, dateFormat: 'Y-m-d H:i:S', time_24hr: true, minDate: 'today' }}
                                onChange={(_, dateStr) => setInviteFormData(prev => ({ ...prev, expires_at: dateStr || '' }))}
                                disabled={sending}
                                placeholder="Select expiry date & time…"
                            />
                            {formErrors.expires_at && <div className="invalid-feedback d-block">{formErrors.expires_at}</div>}
                            <Form.Text className="text-muted">Optional — defaults to 7 days from now.</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="inviteEmailSubject">
                            <Form.Label>Email Subject</Form.Label>
                            <Form.Control
                                type="text"
                                name="email_subject"
                                placeholder="Enter email subject..."
                                value={inviteFormData.email_subject}
                                onChange={handleFormChange}
                                disabled={sending}
                                isInvalid={!!formErrors.email_subject}
                            />
                            <Form.Control.Feedback type="invalid">{formErrors.email_subject}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="inviteEmailContent">
                            <Form.Label>Email Content</Form.Label>
                            <ReactQuill
                                theme="snow"
                                value={inviteFormData.email_content}
                                onChange={handleContentChange}
                                modules={quillModules}
                                readOnly={sending}
                                style={{ background: sending ? '#e9ecef' : '#fff' }}
                            />
                            {formErrors.email_content && (
                                <div className="text-danger small mt-1">{formErrors.email_content}</div>
                            )}
                        </Form.Group>

                        <div className="mb-3">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                type="button"
                                onClick={() => setShowPreview(!showPreview)}
                                disabled={sending}
                            >
                                {showPreview ? 'Hide Preview' : 'Preview Email'}
                            </Button>
                            {showPreview && recipients.length > 1 && (
                                <small className="text-muted ms-2">Previewing with first recipient's details.</small>
                            )}
                        </div>

                        <Collapse in={showPreview}>
                            <div>
                                <Card className="mb-3">
                                    <Card.Header className="bg-light"><strong>Email Preview</strong></Card.Header>
                                    <Card.Body>
                                        <div className="mb-3">
                                            <strong>Subject:</strong>
                                            <div className="mt-2 p-2 bg-light border rounded">
                                                {replaceTokens(inviteFormData.email_subject)}
                                            </div>
                                        </div>
                                        <div>
                                            <strong>Body:</strong>
                                            <div
                                                className="mt-2 p-3 bg-light border rounded"
                                                dangerouslySetInnerHTML={{ __html: replaceTokens(inviteFormData.email_content) }}
                                            />
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </Collapse>

                        <div className="d-flex gap-2">
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={!recipients.some(r => r.email.trim()) || sending}
                            >
                                {sending ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Sending...
                                    </>
                                ) : `Send Invite${recipients.filter(r => r.email.trim()).length > 1 ? 's' : ''}`}
                            </Button>
                            <Button variant="outline-secondary" type="button" onClick={resetForm} disabled={sending}>
                                Reset
                            </Button>
                        </div>
                    </Form>
                </Tab>

                {/* ── Tab 4: Invite Codes ── */}
                <Tab eventKey="codes" title={`Invite Codes${inviteCodesPagination.total > 0 ? ` (${inviteCodesPagination.total})` : ''}`}>
                    <div className="d-flex justify-content-end mb-3">
                        <Button variant="primary" size="sm" onClick={() => handleOpenInviteCodeModal()}>
                            New Invite Code
                        </Button>
                    </div>

                    {inviteCodesError && <Alert variant="danger">{inviteCodesError}</Alert>}

                    {inviteCodesLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    ) : inviteCodes.length === 0 ? (
                        <Alert variant="info">No invite codes yet. Create one to share a reusable join link.</Alert>
                    ) : (
                        <>
                            {inviteCodes.map((ic) => (
                                <Card key={ic.id} className="mb-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                                    <code className="fs-6 fw-bold">{ic.code}</code>
                                                    <Badge bg={ic.is_valid ? 'success' : 'secondary'}>
                                                        {ic.is_valid ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                    {!ic.is_active && <Badge bg="warning" text="dark">Disabled</Badge>}
                                                </div>
                                                <div className="small text-muted">
                                                    <span className="me-3">Uses: <strong>{ic.uses_count} / {ic.redeem_limit}</strong></span>
                                                    {ic.expires_at && <span className="me-3">Expires: <strong>{formatDate(ic.expires_at)}</strong></span>}
                                                    <span>Created: {formatDate(ic.created_at)}</span>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2 flex-shrink-0 ms-3">
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Copy invite code</Tooltip>}>
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={() => { navigator.clipboard.writeText(ic.code); eventBus.emit('toast', { type: 'success', message: 'Invite code copied to clipboard.' }); }}
                                                    >
                                                        Copy code
                                                    </Button>
                                                </OverlayTrigger>
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Copy join URL</Tooltip>}>
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/join?code=${ic.code}`); eventBus.emit('toast', { type: 'success', message: 'Join link copied to clipboard.' }); }}
                                                    >
                                                        Copy link
                                                    </Button>
                                                </OverlayTrigger>
                                                <Button variant="outline-primary" size="sm" onClick={() => handleOpenInviteCodeModal(ic)}>
                                                    Edit
                                                </Button>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}

                            <Row className="mt-3">
                                <Col className="d-flex justify-content-between align-items-center">
                                    <div>
                                        Showing {(inviteCodesPageNumber - 1) * inviteCodesPageSize + 1} to {Math.min(inviteCodesPageNumber * inviteCodesPageSize, inviteCodesPagination.total)} of {inviteCodesPagination.total} codes
                                    </div>
                                    <div>
                                        <Button variant="outline-primary" size="sm" onClick={() => setInviteCodesPageNumber(n => n - 1)} disabled={!inviteCodesPagination.hasPreviousPage} className="me-2">
                                            Previous
                                        </Button>
                                        <span className="mx-2">Page {inviteCodesPageNumber} of {inviteCodesPagination.totalPages}</span>
                                        <Button variant="outline-primary" size="sm" onClick={() => setInviteCodesPageNumber(n => n + 1)} disabled={!inviteCodesPagination.hasNextPage}>
                                            Next
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Tab>
            </Tabs>

            {/* ── Resend Invite Modal ── */}
            <Modal show={showResendModal} onHide={handleCloseResendModal} size="lg" backdrop="static" contentClassName="overflow-hidden">
                {image && (
                    <div style={{ position: 'relative', height: '200px' }}>
                        <img
                            src={image}
                            alt=""
                            className="w-100"
                            style={{ height: '100%', width: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
                        />
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                                boxShadow: 'inset 0 0 60px 18px rgba(0, 0, 0, 0.55)'
                            }}
                        />
                    </div>
                )}
                <Modal.Header closeButton>
                    <Modal.Title>Resend Invite{resendingInvite ? ` — ${resendingInvite.email}` : ''}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleResendSubmit}>
                    <Modal.Body>
                        {resendSaveError && <Alert variant="danger">{resendSaveError}</Alert>}

                        <Form.Group className="mb-3" controlId="resendEmailSubject">
                            <Form.Label>Email Subject</Form.Label>
                            <Form.Control
                                type="text"
                                value={resendFormData.email_subject}
                                onChange={(e) => setResendFormData(prev => ({ ...prev, email_subject: e.target.value }))}
                                disabled={savingResend}
                                isInvalid={!!resendFormErrors.email_subject}
                            />
                            <Form.Control.Feedback type="invalid">{resendFormErrors.email_subject?.[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="resendEmailContent">
                            <Form.Label>Email Content</Form.Label>
                            <ReactQuill
                                theme="snow"
                                value={resendFormData.email_content}
                                onChange={(value) => setResendFormData(prev => ({ ...prev, email_content: value }))}
                                modules={quillModules}
                                readOnly={savingResend}
                                style={{ background: savingResend ? '#e9ecef' : '#fff' }}
                            />
                            {resendFormErrors.email_content && (
                                <div className="text-danger small mt-1">{resendFormErrors.email_content?.[0]}</div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="resendExpiresAt">
                            <Form.Label>Expires At</Form.Label>
                            <Flatpickr
                                className={`form-control${resendFormErrors.expires_at ? ' is-invalid' : ''}`}
                                value={resendFormData.expires_at || undefined}
                                options={{ enableTime: true, dateFormat: 'Y-m-d H:i:S', time_24hr: true, minDate: 'today' }}
                                onChange={(_, dateStr) => setResendFormData(prev => ({ ...prev, expires_at: dateStr || '' }))}
                                disabled={savingResend}
                                placeholder="Select expiry date & time…"
                            />
                            {resendFormErrors.expires_at && <div className="invalid-feedback d-block">{resendFormErrors.expires_at?.[0]}</div>}
                            <Form.Text className="text-muted">Optional — defaults to one week from now if left blank.</Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={handleCloseResendModal} disabled={savingResend}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={savingResend}>
                            {savingResend ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Resending...
                                </>
                            ) : 'Resend Invite'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ── Create Template Modal ── */}
            <Modal show={showTemplateModal} onHide={handleCloseTemplateModal} size="lg" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{editingTemplate ? 'Edit Template' : 'New Invite Template'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveTemplate}>
                    <Modal.Body>
                        {templateSaveError && <Alert variant="danger">{templateSaveError}</Alert>}

                        <Form.Group className="mb-3" controlId="templateName">
                            <Form.Label>Template name *</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                placeholder="e.g. Warm Welcome"
                                value={templateFormData.name}
                                onChange={handleTemplateFormChange}
                                disabled={savingTemplate}
                                isInvalid={!!templateFormErrors.name}
                                required
                            />
                            <Form.Control.Feedback type="invalid">{templateFormErrors.name}</Form.Control.Feedback>
                            <Form.Text className="text-muted">A short internal label — only you see this.</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="templateDescription">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                type="text"
                                name="description"
                                placeholder="e.g. Casual tone, great for community-style clubs"
                                value={templateFormData.description}
                                onChange={handleTemplateFormChange}
                                disabled={savingTemplate}
                                isInvalid={!!templateFormErrors.description}
                            />
                            <Form.Control.Feedback type="invalid">{templateFormErrors.description}</Form.Control.Feedback>
                            <Form.Text className="text-muted">Optional. Shown in the template list to help you pick the right one.</Form.Text>
                        </Form.Group>

                        <Alert variant="info" className="mb-4">
                            <strong>Using placeholders</strong>
                            <p className="mb-2 mt-1 small">
                                Write <code>{'{{placeholder}}'}</code> anywhere in the subject or body and it will automatically be replaced with real data when the invite email is sent. For example, <code>{'{{first_name}}'}</code> becomes <em>Jane</em>.
                            </p>
                            <table className="table table-sm table-borderless mb-0 small">
                                <thead>
                                    <tr>
                                        <th className="p-1">Placeholder</th>
                                        <th>Replaced with</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td className="p-1"><code>{'{{title}}'}</code></td><td>The podcast club's name</td></tr>
                                    <tr><td className="p-1"><code>{'{{first_name}}'}</code></td><td>Recipient's first name (if provided)</td></tr>
                                    <tr><td className="p-1"><code>{'{{email}}'}</code></td><td>Recipient's email address</td></tr>
                                    <tr><td className="p-1"><code>{'{{start_date}}'}</code></td><td>The start date and time of the club's first episode</td></tr>
                                    <tr><td className="p-1"><code>{'{{invite_expiry_date}}'}</code></td><td>The date this invite expires</td></tr>
                                    <tr><td className="p-1"><code>{'{{access_code}}'}</code></td><td>The recipient's unique invite code</td></tr>
                                    <tr><td className="p-1"><code>{'{{invite_url}}'}</code></td><td>Direct link to accept the invite</td></tr>
                                </tbody>
                            </table>
                        </Alert>

                        <Form.Group className="mb-3" controlId="templateSubject">
                            <Form.Label>Email subject line *</Form.Label>
                            <Form.Control
                                type="text"
                                name="subject"
                                placeholder="e.g. You're invited to join {{title}}!"
                                value={templateFormData.subject}
                                onChange={handleTemplateFormChange}
                                disabled={savingTemplate}
                                isInvalid={!!templateFormErrors.subject}
                                required
                            />
                            <Form.Control.Feedback type="invalid">{templateFormErrors.subject}</Form.Control.Feedback>
                            <Form.Text className="text-muted">Placeholders are supported — see the table above.</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="templateContent">
                            <Form.Label>Email body *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={12}
                                name="content"
                                placeholder="e.g. Hi {{first_name}}, you've been invited to join {{title}}. Use your access code {{access_code}} or click here: {{invite_url}}"
                                value={templateFormData.content}
                                onChange={handleTemplateFormChange}
                                disabled={savingTemplate}
                                isInvalid={!!templateFormErrors.content}
                                required
                            />
                            <Form.Control.Feedback type="invalid">{templateFormErrors.content}</Form.Control.Feedback>
                            <Form.Text className="text-muted">Placeholders are supported. Use <code>{'{{invite_url}}'}</code> so recipients can accept in one click.</Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="flex-column align-items-stretch gap-2">
                        <Collapse in={confirmDelete}>
                            <div>
                                <div className="d-flex align-items-center justify-content-between p-2 rounded border border-danger bg-danger bg-opacity-10">
                                    <span className="text-danger small fw-semibold">This template will be permanently deleted.</span>
                                    <div className="d-flex gap-2">
                                        <Button variant="danger" size="sm" onClick={handleDeleteTemplate} disabled={deletingTemplate}>
                                            {deletingTemplate ? (
                                                <><Spinner animation="border" size="sm" className="me-1" />Deleting...</>
                                            ) : 'Yes, delete'}
                                        </Button>
                                        <Button variant="outline-secondary" size="sm" onClick={() => setConfirmDelete(false)} disabled={deletingTemplate}>
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                        <div className="d-flex justify-content-between">
                            <div>
                                {editingTemplate && (
                                    <Button variant="outline-danger" onClick={() => setConfirmDelete(c => !c)} disabled={savingTemplate || deletingTemplate}>
                                        Delete
                                    </Button>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                                <Button variant="secondary" onClick={handleCloseTemplateModal} disabled={savingTemplate || deletingTemplate}>
                                    Cancel
                                </Button>
                                <Button variant="primary" type="submit" disabled={savingTemplate || deletingTemplate}>
                                    {savingTemplate ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Saving...
                                        </>
                                    ) : editingTemplate ? 'Save Changes' : 'Save Template'}
                                </Button>
                            </div>
                        </div>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ── Invite Code Modal ── */}
            <Modal show={showInviteCodeModal} onHide={handleCloseInviteCodeModal} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{editingInviteCode ? 'Edit Invite Code' : 'New Invite Code'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveInviteCode}>
                    <Modal.Body>
                        {inviteCodeSaveError && <Alert variant="danger">{inviteCodeSaveError}</Alert>}

                        <Form.Group className="mb-3" controlId="icCode">
                            <Form.Label>Code *</Form.Label>
                            <Form.Control
                                type="text"
                                name="code"
                                placeholder="e.g. LEADER2026"
                                value={inviteCodeFormData.code}
                                onChange={(e) => setInviteCodeFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                                disabled={savingInviteCode || !!editingInviteCode}
                                isInvalid={!!inviteCodeFormErrors.code}
                                required={!editingInviteCode}
                            />
                            <Form.Control.Feedback type="invalid">{inviteCodeFormErrors.code?.[0]}</Form.Control.Feedback>
                            <Form.Text className="text-muted">
                                {editingInviteCode ? 'Code cannot be changed after creation.' : 'Alphanumeric, dashes and underscores allowed. Stored as uppercase.'}
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="icRedeemLimit">
                            <Form.Label>Redeem limit *</Form.Label>
                            <Form.Control
                                type="number"
                                min="1"
                                name="redeem_limit"
                                value={inviteCodeFormData.redeem_limit}
                                onChange={(e) => setInviteCodeFormData(prev => ({ ...prev, redeem_limit: e.target.value }))}
                                disabled={savingInviteCode}
                                isInvalid={!!inviteCodeFormErrors.redeem_limit}
                                required
                            />
                            <Form.Control.Feedback type="invalid">{inviteCodeFormErrors.redeem_limit?.[0]}</Form.Control.Feedback>
                            <Form.Text className="text-muted">Max number of times this code can be redeemed.</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="icExpiresAt">
                            <Form.Label>Expires at</Form.Label>
                            <Flatpickr
                                className={`form-control${inviteCodeFormErrors.expires_at ? ' is-invalid' : ''}`}
                                value={inviteCodeFormData.expires_at || undefined}
                                options={{ enableTime: true, dateFormat: 'Y-m-d H:i:S', time_24hr: true, minDate: 'today' }}
                                onChange={(_, dateStr) => setInviteCodeFormData(prev => ({ ...prev, expires_at: dateStr || '' }))}
                                disabled={savingInviteCode}
                                placeholder="Select expiry date & time…"
                            />
                            {inviteCodeFormErrors.expires_at && <div className="invalid-feedback d-block">{inviteCodeFormErrors.expires_at?.[0]}</div>}
                            <Form.Text className="text-muted">Optional — leave blank for no expiry.</Form.Text>
                        </Form.Group>

                        <Form.Check
                            type="switch"
                            id="icIsActive"
                            label="Active"
                            checked={inviteCodeFormData.is_active}
                            onChange={(e) => setInviteCodeFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                            disabled={savingInviteCode}
                            className="mb-1"
                        />
                        <Form.Text className="text-muted d-block mb-3">Disable to stop accepting redemptions without deleting the code.</Form.Text>
                    </Modal.Body>
                    <Modal.Footer className="flex-column align-items-stretch gap-2">
                        <Collapse in={confirmDeleteCode}>
                            <div>
                                <Alert variant="danger" className="mb-0 py-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="small">Delete this invite code permanently?</span>
                                        <div className="d-flex gap-2">
                                            <Button variant="outline-secondary" size="sm" onClick={() => setConfirmDeleteCode(false)} disabled={deletingCode}>
                                                Cancel
                                            </Button>
                                            <Button variant="danger" size="sm" onClick={handleDeleteInviteCode} disabled={deletingCode}>
                                                {deletingCode ? <><Spinner animation="border" size="sm" className="me-1" />Deleting…</> : 'Yes, delete'}
                                            </Button>
                                        </div>
                                    </div>
                                </Alert>
                            </div>
                        </Collapse>
                        <div className="d-flex justify-content-between">
                            <div>
                                {editingInviteCode && (
                                    <Button variant="outline-danger" onClick={() => setConfirmDeleteCode(c => !c)} disabled={savingInviteCode || deletingCode}>
                                        Delete
                                    </Button>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                                <Button variant="secondary" onClick={handleCloseInviteCodeModal} disabled={savingInviteCode || deletingCode}>
                                    Cancel
                                </Button>
                                <Button variant="primary" type="submit" disabled={savingInviteCode || deletingCode}>
                                    {savingInviteCode ? (
                                        <><Spinner animation="border" size="sm" className="me-2" />Saving…</>
                                    ) : editingInviteCode ? 'Save Changes' : 'Create Code'}
                                </Button>
                            </div>
                        </div>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ── Import Recipients Modal ── */}
            <Modal show={showImportModal} onHide={handleCloseImportModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Import Recipients</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info" className="mb-4">
                        <strong>Expected file format</strong>
                        <p className="mb-2 mt-1 small">
                            Upload a <strong>.csv</strong> or <strong>.xlsx</strong> file where the <strong>first row contains column headers</strong>. The <code>email</code> column is required; <code>first_name</code> and <code>last_name</code> are optional.
                        </p>
                        <table className="table table-sm table-borderless mb-0 small">
                            <thead>
                                <tr>
                                    <th className="p-1">email</th>
                                    <th className="p-1">first_name</th>
                                    <th className="p-1">last_name</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="p-1"><code>jane@example.com</code></td>
                                    <td className="p-1"><code>Jane</code></td>
                                    <td className="p-1"><code>Doe</code></td>
                                </tr>
                                <tr>
                                    <td className="p-1"><code>john@example.com</code></td>
                                    <td className="p-1"><code>John</code></td>
                                    <td className="p-1"><code>Smith</code></td>
                                </tr>
                            </tbody>
                        </table>
                    </Alert>

                    <div className="mb-3">
                        <Button
                            variant="outline-primary"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Choose file…
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            className="d-none"
                            onChange={handleFileImport}
                        />
                    </div>

                    {importError && (
                        <Alert variant="danger" dismissible onClose={() => setImportError(null)}>
                            {importError}
                        </Alert>
                    )}

                    {importPreview && (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>
                                    Preview — {importPreview.length} recipient{importPreview.length !== 1 ? 's' : ''} found
                                    {importPreview.length > 50 && <span className="text-warning ms-2">(only first 50 will be imported)</span>}
                                </strong>
                            </div>
                            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                                <table className="table table-sm table-bordered mb-0 small">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>#</th>
                                            <th>Email</th>
                                            <th>First name</th>
                                            <th>Last name</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {importPreview.slice(0, 50).map((r, i) => (
                                            <tr key={i}>
                                                <td className="text-muted">{i + 1}</td>
                                                <td>{r.email}</td>
                                                <td>{r.first_name || <span className="text-muted">—</span>}</td>
                                                <td>{r.last_name || <span className="text-muted">—</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseImportModal}>Cancel</Button>
                    <Button
                        variant="primary"
                        onClick={handleImportConfirm}
                        disabled={!importPreview}
                    >
                        Import {importPreview ? `${Math.min(importPreview.length, 50)} recipient${Math.min(importPreview.length, 50) !== 1 ? 's' : ''}` : ''}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ClubInvites;
